









































































import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
// components
import FileSelectModal from "@/components/files/modals/FileSelectModal.vue";
import SelectStockImageModal from "@/components/files/modals/SelectStockImageModal.vue";
import BehaviorDropdown from "@/components/posts/BehaviorDropdown.vue";
import DirectFileUploadControl from "@/components/common/files/DirectFileUploadControl.vue";
import VueCropperAdjust from "@/components/common/cropperjs/VueCropperAdjust.vue";

// types
import { BehaviorEnum } from "@/services/posts/types";
import { ImageIPostProperties, ImagePostSettings } from "@/services/posts/postTypes/image";
import { FILEENDPOINT } from "@/app.config";
import { MediaType } from "@/services/files/types";
import i18n from "@/i18n";
import { ValidationModel } from "@/services/validation/types";
import { FolderEntites } from "@/services/folders/types";
import { Getter } from "vuex-class";
import { getFileFolderEntity } from "@/services/files/fileService";
import { ContainerDimensions } from "@/services/posts/postTypes/fixedhtml";

const namespace: string = "authentication";

@Component({
    components: {
        DirectFileUploadControl,
        FileSelectModal,
        BehaviorDropdown,
        SelectStockImageModal,
        VueCropperAdjust
    }
})
export default class ImageForm extends Vue {
    @Getter("getUserHasFolderAccess", { namespace }) getUserHasFolderAccess!: boolean;
    @Getter("getAllowStockImages") allowStockImages!: boolean;
    @Prop() private properties!: ImageIPostProperties;
    @Prop() private channelHeight!: number;
    @Prop() private channelWidth!: number;
    @Prop() postSettings!: ImagePostSettings;
    @Ref("cropperAdjust") cropperAdjust!: VueCropperAdjust;

    ratio = 1;
    factor = 1;
    allowDrag = false;
    allowResize = false;
    originalFileName = "" as string;
    fileFolderEntity = FolderEntites.Files as FolderEntites;

    containerSize = {
        width: 0,
        height: 0,
        origWidth: 0,
        origHeight: 0
    };

    imageSize = {
        width: 0,
        height: 0,
        origWidth: 0,
        origHeight: 0
    };

    img = {
        width: 0,
        height: 0
    };

    // local state
    localProperties = { path: "", name: "", behavior: "" as BehaviorEnum } as ImageIPostProperties;
    mediaTypes = [MediaType.Image];
    behaviorTypes: any = Object.keys(BehaviorEnum)
        .map(key => ({ name: i18n.t(`posts.${key}`), id: key }));

    validators = {
        behavior: {
            active: null
        } as unknown as ValidationModel,
        image: {
            active: null
        } as unknown as ValidationModel
    };

    folders = FolderEntites;
    // lifecycle hooks
    created() {
        this.localProperties.behavior = "adjust" as BehaviorEnum;
    }

    @Watch("localProperties.behavior", { immediate: true }) localPropWatch() {
        this.updateContainerAndImageSize();
    }

    async mounted() {
        if (this.properties && this.properties.path) {
            this.localProperties = this.properties;
            this.loadImage();
            this.fileFolderEntity = await getFileFolderEntity(this.properties.path);
        } else {
            this.$nextTick(() => {
                this.$emit("setDefaultValues", JSON.stringify(this.localProperties));
            });
        }
    }

    // computed
    get imageUrl(): string {
        return `${FILEENDPOINT}${this.localProperties.path}`;
    }

    get behaviorValid(): boolean {
        return this.localProperties.behavior.length > 0;
    }

    get imageValid(): boolean {
        return this.localProperties.path.length > 0;
    }

    // Used for the vue cropper adjust component
    get containerDimensions(): ContainerDimensions {
        return {
            width: this.channelWidth,
            height: this.channelHeight
        };
    }

    // methods
    openFileSelect() {
        if (this.fileFolderEntity === FolderEntites.Files && this.getUserHasFolderAccess) {
            (this.$refs.fileSelectModal as any).open(this.localProperties.path);
        } else if (this.fileFolderEntity === FolderEntites.StockFiles && this.allowStockImages) {
            (this.$refs.selectStockImageModal as any).open();
        } else {
            (this.$refs.directFileUploadControl as any).openFileUpload();
        }
    }

    openStockImageSelect() {
        (this.$refs.selectStockImageModal as any).open();
    }

    removeImage() {
        this.fileFolderEntity = FolderEntites.Files;
        this.localProperties.path = "";
        this.localProperties.name = "";

        this.toggleHeaderButtons(true);
    }

    // Load the image and set its dimensions, then calculate aspect ratio and update container & image sizes
    loadImage() {
        const image = new Image();
        image.onload = () => {
            // Set image dimensions
            this.img.width = image.width;
            this.img.height = image.height;
            // Calculate aspect ratio and update container and image sizes
            this.calculateAspectRatio(500, 500, image);
            this.updateContainerAndImageSize();
        };
        image.src = this.imageUrl;
    }

    // Calculate the aspect ratio and set the container and image sizes accordingly
    calculateAspectRatio(maxWidth: number, maxHeight: number, image: HTMLImageElement) {
        const ratio = Math.min(maxWidth / this.channelWidth, maxHeight / this.channelHeight, 1);
        const scaledWidth = this.channelWidth * ratio;
        const scaledHeight = this.channelHeight * ratio;

        // Set container and image sizes based on the calculated ratio
        this.containerSize = { width: scaledWidth, height: scaledHeight, origWidth: scaledWidth, origHeight: scaledHeight };
        this.imageSize = { width: image.width * ratio, height: image.height * ratio, origWidth: image.width * ratio, origHeight: image.height * ratio };
    }

    updateContainerAndImageSize() {
        switch (this.localProperties.behavior) {
        // for the adjust behavior, set the container size to max 500x500
        case BehaviorEnum.adjust:
            this.containerSize.width = 500;
            this.containerSize.height = 500;
            break;
        case BehaviorEnum.crop:
            this.updateImageSizeForCrop();
            break;
        case BehaviorEnum.letterBox:
            this.updateImageSizeForLetterBox();
            break;
        default:
            this.containerSize.width = this.containerSize.origWidth;
            this.containerSize.height = this.containerSize.origHeight;
            this.imageSize.width = this.imageSize.origWidth;
            this.imageSize.height = this.imageSize.origHeight;
        }
    }

    updateImageSizeForCrop() {
        this.imageSize.width = this.imageSize.origWidth;
        this.imageSize.height = this.imageSize.origHeight;
        this.containerSize.width = this.containerSize.origWidth;
        this.containerSize.height = this.containerSize.origHeight;
        if (this.imageSize.origWidth > this.containerSize.width && this.imageSize.origHeight > this.containerSize.height) {
            this.imageSize.width = this.containerSize.width;
            this.imageSize.height = this.containerSize.height;
        }
    }

    updateImageSizeForLetterBox() {
        this.containerSize.width = this.containerSize.origWidth;
        this.containerSize.height = this.containerSize.origHeight;
        const { origWidth, origHeight } = this.imageSize;
        if (origWidth > this.containerSize.width || origHeight > this.containerSize.height) {
            const ratio = Math.min(
                this.containerSize.width / origWidth,
                this.containerSize.height / origHeight
            );
            this.imageSize.width = origWidth * ratio;
            this.imageSize.height = origHeight * ratio;
        }
    }

    onSelect(file: any) {
        this.$set(this.localProperties, "path", file.fileId);
        this.$set(this.localProperties, "name", file.name);
        this.setItemName();
        this.loadImage();
        this.fileFolderEntity = FolderEntites.Files;
        this.$bvModal.hide("modal-fileSelect");
    }

    onDirectFileUploaded(file: any) {
        this.$set(this.localProperties, "path", file.fileId);
        this.$set(this.localProperties, "name", file.originalFileName);
        this.originalFileName = file.originalFileName;
        this.setItemName();
        this.loadImage();
        this.fileFolderEntity = FolderEntites.DirectFiles;
    }

    onStockFileUploaded(file: any) {
        this.$set(this.localProperties, "path", file.fileId);
        this.$set(this.localProperties, "name", file.originalFileName);
        this.originalFileName = file.originalFileName;
        this.setItemName();
        this.loadImage();
        this.fileFolderEntity = FolderEntites.StockFiles;
    }

    uploadStarted() {
        this.localProperties.path = "";
        this.localProperties.name = "";
    }

    validateData() {
        this.validators.behavior.active = true;
        this.validators.image.active = true;
        return this.behaviorValid && this.imageValid;
    }

    emitData() {
        // update properties for the adjust behavior with path.
        if (this.localProperties.path && this.localProperties.behavior === "adjust") {
            this.cropperAdjust.updateProperties();
        }
        this.$emit("propertiesUpdated", this.localProperties);
    }

    onPropertiesUpdated(properties: ImageIPostProperties) {
        this.localProperties.position = properties.position;
    }

    setItemName() {
        const name =
            this.localProperties.caption ||
            this.originalFileName.substring(0, this.originalFileName.lastIndexOf(".")) ||
            this.localProperties.name.substring(0, this.localProperties.name.lastIndexOf("."));
        this.$emit("itemNameUpdated", name);
    }

    toggleHeaderButtons(state: boolean) {
        this.$emit("toggleHeaderButtons", state);
    }
}
